// QMG Blueprint symbol assets for blog embeds and downloads.
// Icons use the same Lucide paths as the in-app QMGSymbols component so branding stays consistent.

const AWARENESS_SVG_INNER = `<path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/><path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"/><path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"/><path d="M17.599 6.5a3 3 0 0 0 .399-1.375"/><path d="M6.003 5.125A3 3 0 0 0 6.401 6.5"/><path d="M3.477 10.896a4 4 0 0 1 .585-.396"/><path d="M19.938 10.5a4 4 0 0 1 .585.396"/><path d="M6 18a4 4 0 0 1-1.967-.516"/><path d="M19.967 17.484A4 4 0 0 1 18 18"/>`;
const ACCEPTANCE_SVG_INNER = `<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z"/>`;
const ACTION_SVG_INNER = `<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>`;

const buildSvg = (inner, size = 48) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;

export const SYMBOLS = [
  { key: 'awareness', label: 'Awareness', inner: AWARENESS_SVG_INNER },
  { key: 'acceptance', label: 'Acceptance', inner: ACCEPTANCE_SVG_INNER },
  { key: 'action', label: 'Action', inner: ACTION_SVG_INNER },
];

/* ------------------ Standalone SVG for download ------------------ */
// A filled dark-green disc + white icon. 200×260 (icon + label).
const buildSingleDownloadSvg = (label, innerPath) => `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="200" height="260" viewBox="0 0 200 260">
  <circle cx="100" cy="100" r="80" fill="#1A2E1D"/>
  <g transform="translate(60 60) scale(3.3)" fill="none" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${innerPath}</g>
  <text x="100" y="230" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-size="20" font-weight="700" letter-spacing="3" fill="#1A2E1D">${label.toUpperCase()}</text>
</svg>`;

/* ------------------ Combined triad SVG for download ------------------ */
const buildTriadDownloadSvg = () => {
  const disc = (cx, label, innerPath) => `
    <g>
      <circle cx="${cx}" cy="100" r="56" fill="#1A2E1D"/>
      <g transform="translate(${cx - 24} ${100 - 24}) scale(2)" fill="none" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${innerPath}</g>
      <text x="${cx}" y="200" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-size="18" font-weight="700" letter-spacing="3" fill="#1A2E1D">${label.toUpperCase()}</text>
    </g>`;
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="720" height="260" viewBox="0 0 720 260">
  <line x1="216" y1="100" x2="288" y2="100" stroke="#1A2E1D" stroke-opacity="0.3" stroke-width="2"/>
  <line x1="432" y1="100" x2="504" y2="100" stroke="#1A2E1D" stroke-opacity="0.3" stroke-width="2"/>
  ${disc(144, 'Awareness', AWARENESS_SVG_INNER)}
  ${disc(360, 'Acceptance', ACCEPTANCE_SVG_INNER)}
  ${disc(576, 'Action', ACTION_SVG_INNER)}
</svg>`;
};

export const DOWNLOADS = [
  { name: 'qmg-awareness.svg', svg: buildSingleDownloadSvg('Awareness', AWARENESS_SVG_INNER) },
  { name: 'qmg-acceptance.svg', svg: buildSingleDownloadSvg('Acceptance', ACCEPTANCE_SVG_INNER) },
  { name: 'qmg-action.svg', svg: buildSingleDownloadSvg('Action', ACTION_SVG_INNER) },
  { name: 'qmg-blueprint-triad.svg', svg: buildTriadDownloadSvg() },
];

/* ------------------ Blog embed HTML snippet ------------------ */
// This HTML is inserted into the TipTap editor and rendered back via dangerouslySetInnerHTML
// on the public blog page. Styles are attached via a scoped class in index.css so the
// block looks correct even when the page typography changes.
export const buildBlogEmbed = () => {
  const chip = (label, inner) => `
    <span class="qmg-embed-item">
      <span class="qmg-embed-disc">${buildSvg(inner, 28)}</span>
      <span class="qmg-embed-label">${label}</span>
    </span>`;
  return `<div class="qmg-embed-block" data-qmg-embed="true">
  <p class="qmg-embed-heading">The QMG Blueprint</p>
  <div class="qmg-embed-row">
    ${chip('Awareness', AWARENESS_SVG_INNER)}
    <span class="qmg-embed-sep"></span>
    ${chip('Acceptance', ACCEPTANCE_SVG_INNER)}
    <span class="qmg-embed-sep"></span>
    ${chip('Action', ACTION_SVG_INNER)}
  </div>
</div>`;
};

/* ------------------ Download helper ------------------ */
export const triggerSvgDownload = (filename, svgString) => {
  const blob = new Blob([svgString], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const downloadAll = () => {
  DOWNLOADS.forEach((f, i) => {
    setTimeout(() => triggerSvgDownload(f.name, f.svg), i * 250);
  });
};
